import { useFetcher, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button } from '~/common/components/ui/button';
import { UserRound } from 'lucide-react';
import { cn } from '~/common/utils/styleUtil';
import { Tooltip, TooltipTrigger, TooltipContent } from '~/common/components/ui/tooltip.tsx';

export interface LoginButtonProps {
  variant?: 'default' | 'hint';
  className?: string;
  size?: 'default' | 'sm' | 'lg';
  hideIcon?: boolean;
  redirectPath?: string;
  showAghTooltip?: boolean;
}

export function LoginButton({
  variant = 'default',
  className,
  size,
  hideIcon,
  redirectPath,
  showAghTooltip = false
}: Readonly<LoginButtonProps>) {
  const fetcher = useFetcher();
  const location = useLocation();
  const { t } = useTranslation();
  const isHint = variant === 'hint';
  const isSmallButton = size !== 'lg' && !!(size ?? isHint);
  const btnSize = isSmallButton ? 'sm' : 'default';

  const button = (
    <Button
      variant={isHint ? 'link' : 'default'}
      size={btnSize}
      className={cn('w-full', size === 'lg' ? 'h-11' : 'h-10')}
      data-testid={'login-button'}
      onClick={e => e.stopPropagation()}
    >
      {!isHint && !hideIcon && <UserRound size={18} className="mr-2" />}
      <p className={'break-words whitespace-normal'}>
        {t(isHint ? 'Bitte loggen Sie sich ein, um diesen Artikel zu bestellen' : 'Anmelden')}
      </p>
    </Button>
  );

  return (
    <fetcher.Form method={'POST'} action={'/infra/auth/login'} className={className}>
      <input type="hidden" name="redirectUrl" value={redirectPath ?? location.pathname + location.search} />
      {showAghTooltip ? (
        <Tooltip>
          <TooltipTrigger asChild={true}>{button}</TooltipTrigger>
          <TooltipContent
            showArrow={true}
            sideOffset={-4}
            side={'left'}
            className={'py-4 bg-secondary text-white max-w-56'}
          >
            {t('Nutzen Sie Ihr Kundenportal-Login für die Anmeldung')}
          </TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </fetcher.Form>
  );
}
